import { useMutation } from "@tanstack/react-query";
import { UseFormSetError } from "react-hook-form";
import { t } from "i18next";

import { DOMAIN_AUTH } from "../domain";

export const ACTION_LOGIN = "LOGIN";

interface LoginDto {
  username: string;
  password: string;
  otp?: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
}

interface LoginApiError {
  message?: string[];
  error?: string;
  statusCode?: number;
}

const loginRequest = async ({
  username,
  password,
  otp,
}: LoginDto): Promise<LoginResponse> => {
  const params = new URLSearchParams({
    username,
    password,
  });

  if (otp) {
    params.append("otp", otp);
  }

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: params,
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    throw {
      name: "LoginError",
      message: "Login failed",
      response: responseBody,
      status: response.status,
    };
  }

  localStorage.setItem("access_token", responseBody.access_token);
  localStorage.setItem("refresh_token", responseBody.refresh_token);
  window.dispatchEvent(new Event("access_token_change"));

  return responseBody;
};

interface UseLoginParams {
  setError?: UseFormSetError<{ email: string; password: string; otp?: string }>;
}

export const useLogin = ({ setError }: UseLoginParams = {}): {
  login: ReturnType<
    typeof useMutation<
      LoginResponse,
      { response?: LoginApiError; message: string },
      LoginDto
    >
  >["mutate"];
  isPendingLogin: boolean;
  isSuccessLogin: boolean;
  loginError: unknown;
} => {
  type LoginError = {
    response?: LoginApiError;
    message: string;
  };

  const { mutate, isPending, isSuccess, error } = useMutation<
    LoginResponse,
    LoginError,
    LoginDto
  >({
    mutationKey: [DOMAIN_AUTH, ACTION_LOGIN],
    mutationFn: loginRequest,
    onError: (error) => {
      const messages = error.response?.message;

      if (!setError) return;

      if (Array.isArray(messages)) {
        if (messages.includes("invalid_credentials")) {
          setError("email", {
            type: "server",
            message: t("auth.invalidCredentials"),
          });
          setError("password", {
            type: "server",
            message: t("auth.invalidCredentials"),
          });
        }

        if (messages.includes("otp_required")) {
          setError("otp", {
            type: "server",
            message: t("auth.otpRequired"),
          });
        }
      } else {
        setError("email", {
          type: "server",
          message: t("login.invalid_username_or_password"),
        });
      }
    },
  });

  return {
    login: mutate,
    isPendingLogin: isPending,
    isSuccessLogin: isSuccess,
    loginError: error,
  };
};
